/**
 * Admin Vehicles API client.
 *
 * Network-layer surface for the slim Phase 2 admin vehicles workflow
 * (CON-VEH-001). Wraps the platform endpoints under
 * `/api/v1/admin/vehicles/*`:
 *
 *   - GET    /admin/vehicles            → {@link listAdminVehicles}
 *   - GET    /admin/vehicles/{id}       → {@link getAdminVehicle}
 *   - POST   /admin/vehicles            → {@link createAdminVehicle}
 *   - PUT    /admin/vehicles/{id}       → {@link updateAdminVehicle}
 *   - DELETE /admin/vehicles/{id}       → {@link deleteAdminVehicle}
 *
 * Plus a Console-side bulk helper, {@link bulkUpdateAdminVehicles}, that
 * coalesces a list of partial updates into one call.
 *
 * Bulk endpoint note: PL-VEH-002 ships the per-row CRUD endpoints but does
 * NOT include a `/admin/vehicles/bulk-update` endpoint. The client-side
 * {@link bulkUpdateAdminVehicles} therefore fans out the bulk operation
 * into N parallel `PUT /admin/vehicles/{id}` calls. The container layer
 * uses a single helper so the day a real `POST /admin/vehicles/bulk-update`
 * lands, we can swap the implementation here without touching any view.
 *
 * Every helper:
 *   1. Calls the configured shared axios instance with the right path,
 *      method, and body — no per-call auth headers, no per-call baseURL.
 *   2. Unwraps the success envelope into the typed payload.
 *   3. Translates any non-2xx response into an {@link ApiError} that
 *      preserves the platform envelope's `status`, `severity`, `message`,
 *      `errors` map (422), and `error_code`.
 */
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  AdminVehicleListData,
  AdminVehicleListParams,
  PaginationMeta,
  Vehicle,
  VehicleData,
  VehicleType,
  VehicleWritePayload,
} from '@dfq/shared/types/vehicle';

import { apiClient } from './client';
import { throwAsApiError, unwrap } from './_envelope';

const COLLECTION_PATH = '/admin/vehicles';

function itemPath(id: number): string {
  return `${COLLECTION_PATH}/${encodeURIComponent(String(id))}`;
}

/**
 * Translate a Console-shaped {@link AdminVehicleListParams} into the flat
 * query-string surface the platform's `QueryParameters::fromRequest`
 * expects.
 *
 * - `filters.*` keys are emitted as `filters[<column>]=value`.
 * - Multi-value filters (e.g. `vehicleType: ['suv', 'pickup']`) emit as
 *   `filters[vehicle_type][]=suv&filters[vehicle_type][]=pickup`.
 * - Snake-case keys are used at the wire boundary to match the platform's
 *   column names.
 */
function buildListParams(
  params: AdminVehicleListParams,
): Record<string, string | number | boolean | (string | number | boolean)[]> {
  const out: Record<string, string | number | boolean | (string | number | boolean)[]> = {};

  if (params.page !== undefined) out.page = params.page;
  if (params.perPage !== undefined) out.per_page = params.perPage;
  if (params.sortBy !== undefined) out.sort_by = params.sortBy;
  if (params.sortDirection !== undefined) out.sort_direction = params.sortDirection;

  const filters = params.filters ?? {};
  if (filters.year !== undefined) out['filters[year]'] = filters.year;
  if (filters.make !== undefined && filters.make !== '') out['filters[make]'] = filters.make;
  if (filters.model !== undefined && filters.model !== '') out['filters[model]'] = filters.model;
  if (filters.isActive !== undefined) out['filters[is_active]'] = filters.isActive;
  if (filters.search !== undefined && filters.search !== '') {
    out['filters[search]'] = filters.search;
  }
  if (filters.vehicleType !== undefined) {
    if (Array.isArray(filters.vehicleType)) {
      if (filters.vehicleType.length > 0) {
        out['filters[vehicle_type]'] = [...filters.vehicleType];
      }
    } else {
      out['filters[vehicle_type]'] = filters.vehicleType;
    }
  }

  return out;
}

/**
 * Paginated result returned by {@link listAdminVehicles}. Pulls the
 * envelope's `meta.pagination` block alongside the typed data so containers
 * can drive page count / "no results" affordances without hand-rolling
 * envelope inspection.
 */
export interface AdminVehicleListResult {
  vehicles: Vehicle[];
  pagination: PaginationMeta;
}

const DEFAULT_PAGINATION: PaginationMeta = {
  page: 1,
  per_page: 25,
  total: 0,
  last_page: 1,
  has_more: false,
};

/**
 * `GET /api/v1/admin/vehicles`
 *
 * Paginated list of vehicles for the Console admin grid. Translates
 * {@link AdminVehicleListParams} into the platform's `QueryParameters`
 * surface (filters, sort, page).
 */
export async function listAdminVehicles(
  params: AdminVehicleListParams = {},
): Promise<AdminVehicleListResult> {
  try {
    const response = await apiClient.get<ApiEnvelope<AdminVehicleListData>>(COLLECTION_PATH, {
      params: buildListParams(params),
    });
    const data = unwrap(response.data, response.status);
    const paginationRaw = response.data.meta?.pagination;
    const pagination =
      paginationRaw && typeof paginationRaw === 'object'
        ? (paginationRaw as PaginationMeta)
        : DEFAULT_PAGINATION;
    return {
      vehicles: data.vehicles ?? [],
      pagination,
    };
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * `GET /api/v1/admin/vehicles/{id}`
 *
 * Fetch a single vehicle by id. Returns the API-shaped {@link Vehicle}
 * including the derived `defaultSqftEstimate` so the same payload powers
 * both the admin edit form and the storefront prefill.
 */
export async function getAdminVehicle(id: number): Promise<Vehicle> {
  try {
    const response = await apiClient.get<ApiEnvelope<VehicleData>>(itemPath(id));
    const data = unwrap(response.data, response.status);
    if (!data.vehicle) {
      throw new Error('Vehicle envelope missing `data.vehicle`');
    }
    return data.vehicle;
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * `POST /api/v1/admin/vehicles`
 *
 * Create a new vehicle. Validation failures return 422 with the
 * field-keyed `errors` map (which surfaces on the thrown {@link ApiError}).
 */
export async function createAdminVehicle(payload: VehicleWritePayload): Promise<Vehicle> {
  try {
    const response = await apiClient.post<ApiEnvelope<VehicleData>>(COLLECTION_PATH, payload);
    const data = unwrap(response.data, response.status);
    if (!data.vehicle) {
      throw new Error('Vehicle envelope missing `data.vehicle`');
    }
    return data.vehicle;
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * `PUT /api/v1/admin/vehicles/{id}`
 *
 * Update an existing vehicle. Per the platform's `UpdateVehicleRequest`,
 * every field on {@link VehicleWritePayload} is `sometimes` so callers can
 * pass partial payloads (the bulk helper relies on this).
 */
export async function updateAdminVehicle(
  id: number,
  payload: Partial<VehicleWritePayload>,
): Promise<Vehicle> {
  try {
    const response = await apiClient.put<ApiEnvelope<VehicleData>>(itemPath(id), payload);
    const data = unwrap(response.data, response.status);
    if (!data.vehicle) {
      throw new Error('Vehicle envelope missing `data.vehicle`');
    }
    return data.vehicle;
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * `DELETE /api/v1/admin/vehicles/{id}`
 *
 * Hard-delete a vehicle. Returns void on success; 4xx/5xx surface as
 * {@link ApiError}.
 */
export async function deleteAdminVehicle(id: number): Promise<void> {
  try {
    const response = await apiClient.delete<ApiEnvelope<Record<string, never>>>(itemPath(id));
    unwrap(response.data, response.status);
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * Apply a single partial update to multiple vehicles at once.
 *
 * Today this fans out into N parallel `PUT /admin/vehicles/{id}` calls
 * because the platform does not yet expose a `POST /admin/vehicles/bulk-update`
 * endpoint. Container code calls this helper rather than orchestrating the
 * fan-out itself, so the bulk endpoint can land without a Console change.
 *
 * Resolves once every per-row request has settled. The first failure (if
 * any) is rethrown as an {@link ApiError}; the result list contains the
 * fresh {@link Vehicle} payloads for every row that succeeded so the
 * container can patch its cache without a refetch.
 */
export interface BulkUpdateAdminVehiclesParams {
  ids: number[];
  patch: Partial<VehicleWritePayload>;
}

export async function bulkUpdateAdminVehicles({
  ids,
  patch,
}: BulkUpdateAdminVehiclesParams): Promise<Vehicle[]> {
  if (ids.length === 0) return [];

  const results = await Promise.allSettled(
    ids.map((id) => updateAdminVehicle(id, patch)),
  );

  const updated: Vehicle[] = [];
  let firstRejection: unknown;
  for (const settled of results) {
    if (settled.status === 'fulfilled') {
      updated.push(settled.value);
    } else if (firstRejection === undefined) {
      firstRejection = settled.reason;
    }
  }

  if (firstRejection !== undefined) {
    // The per-row `updateAdminVehicle` already projected to ApiError; just
    // rethrow so the caller's catch block sees a typed failure.
    throw firstRejection;
  }

  return updated;
}

/**
 * Convenience exports — keep the named-API surface flat so consumers can
 * write `import { listAdminVehicles } from '@dfq/shared/api/adminVehicles'`.
 *
 * The `VehicleType` re-export is here so containers importing the API
 * module (e.g. for the bulk dialog) don't also have to reach into the
 * types module for the union — one import, one source of truth.
 */
export type { VehicleType };
